'use client';
import { useStockPeek } from './StockSnapshotContext';

/* The one line shown in the collapsed mobile drawer. It answers the shopper's
   first two questions before the drawer opens — "is this the time to buy?"
   (trend sparkline + verdict) and "do they have it?" (cars on lots) — and ends
   with a "See more" teaser so it reads as a preview, not a caption.

   The verdict is the time-on-lot tag ("Steady but slowing") when the model
   has one, since it says more than the supply change ("▼31% since Jun"); the
   opened drawer's first card repeats the tag and explains it.

   Models without trend history show the supply mini-bar instead of the
   sparkline. The filtered count lives in the row under the filter bar, so
   this line always speaks for the whole market. Styles: tokens.css
   `.stock-peek*`. Rendered inside TabsDrawer's `peek` button and, as a
   stand-in, inside the loading shell. */
export function StockPeekLine() {
  const peek = useStockPeek();
  if (!peek) return null;
  const fmt = (n: number) => n.toLocaleString('en-US');
  const count =
    peek.onLot > 0 ? (
      <>
        <b>{fmt(peek.onLot)}</b> on lots
      </>
    ) : (
      <>
        <b>{fmt(peek.total)}</b> listed
      </>
    );

  return (
    <span className="stock-peek">
      {peek.trend ? (
        <>
          <Sparkline points={peek.trend.points} color={peek.trend.accent} />
          <span className="stock-peek-text">
            {count} · {peek.pace ?? peek.trend.label}
          </span>
        </>
      ) : (
        <>
          <span className="stock-peek-text">
            {count}
            {peek.pace
              ? ` · ${peek.pace}`
              : peek.onLot > 0 && peek.onLot < peek.total
                ? ` · ${fmt(peek.total)} listed`
                : ''}
          </span>
          {peek.supply.length > 1 && (
            <span className="stock-peek-seg stock-snapshot" aria-hidden="true">
              {peek.supply.map((x) => (
                <i
                  key={x.key}
                  data-k={x.key}
                  style={{ flexGrow: x.count, flexBasis: 0 }}
                />
              ))}
            </span>
          )}
        </>
      )}
      <span className="stock-peek-more">See more ›</span>
    </span>
  );
}

const SPARK_W = 90;
const SPARK_H = 18;

function Sparkline({ points, color }: { points: number[]; color: string }) {
  if (points.length < 2) return null;
  const min = Math.min(...points);
  const span = Math.max(...points) - min || 1;
  const xy = points.map((v, i) => {
    const x = (i / (points.length - 1)) * (SPARK_W - 4) + 1;
    const y = SPARK_H - 3 - ((v - min) / span) * (SPARK_H - 6);
    return [x, y] as const;
  });
  const [lx, ly] = xy[xy.length - 1];
  return (
    <svg
      className="stock-peek-spark"
      width={SPARK_W}
      height={SPARK_H}
      viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
      aria-hidden="true"
    >
      <polyline
        points={xy.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ')}
        fill="none"
        stroke={color}
        strokeWidth="1.6"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={lx} cy={ly} r="2" fill={color} />
    </svg>
  );
}
